import content from "./content.json";
import { ARCHETYPE_ACCENTS } from "./chrome";

/**
 * Brief §4/§7 — intro screen: eyebrow/title/intro copy, meta row (time ·
 * questions · instant result), audience note, a 4-card archetype preview
 * (names only, no result leakage), the legal/disclaimer note, and — when
 * a saved in-progress run exists — the resume-or-start-fresh choice.
 * Language now comes from the site's own /pt/ /el/ locale (`DiagnosticLayout`
 * provides the switcher in its header) rather than an in-page toggle.
 */
export const IntroScreen = ({ language, onStart, savedProgress, onResume, onStartFresh }) => {
  const ui = content.content[language].ui;

  return (
    <div className="max-w-2xl mx-auto px-5 md:px-8 py-14 md:py-20" data-testid="pa-intro-screen">
      {savedProgress && (
        <div className="rounded-xl border border-[#b74e33]/30 bg-[#b74e33]/5 px-5 py-4 mb-8" data-testid="pa-resume-banner">
          <p className="text-sm text-[#263b3d] mb-3">{ui.resume}</p>
          <div className="flex gap-3">
            <button
              type="button"
              data-testid="pa-resume-continue"
              onClick={onResume}
              className="px-5 py-2 rounded-full text-sm font-semibold text-white bg-[#b74e33] hover:bg-[#9c3f29] transition-colors"
            >
              {ui.continue}
            </button>
            <button
              type="button"
              data-testid="pa-resume-fresh"
              onClick={onStartFresh}
              className="px-5 py-2 rounded-full text-sm font-semibold text-[#263b3d] border border-[#263b3d]/20 hover:border-[#263b3d]/40 transition-colors"
            >
              {ui.fresh}
            </button>
          </div>
        </div>
      )}

      <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#607e66] mb-3">{ui.eyebrow}</p>
      <h1 className="font-serif text-3xl md:text-4xl text-[#263b3d] leading-tight mb-4" data-testid="pa-intro-title">
        {ui.title}
      </h1>
      <p className="text-base text-[#3f4a47] leading-relaxed mb-5">{ui.intro}</p>

      <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-[#6b6b63] mb-6">
        <span>{ui.time}</span>
        <span aria-hidden="true">&middot;</span>
        <span>{ui.count}</span>
        <span aria-hidden="true">&middot;</span>
        <span>{ui.instant}</span>
      </div>

      <p className="text-sm text-[#6b6b63] mb-10">{ui.audience}</p>

      <p className="text-xs font-semibold uppercase tracking-wide text-[#263b3d] mb-3">{ui.how}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="pa-archetype-preview-grid">
        {content.archetypeOrder.map((id) => {
          const profile = content.content[language].profiles[id];
          return (
            <div
              key={id}
              data-testid={`pa-archetype-card-${id}`}
              className="rounded-xl border border-black/10 bg-white px-3 py-4 text-center"
            >
              <div
                className="w-2 h-2 rounded-full mx-auto mb-2"
                style={{ background: ARCHETYPE_ACCENTS[id] }}
                aria-hidden="true"
              />
              <p className="text-sm font-semibold text-[#263b3d] leading-tight">{profile.name}</p>
            </div>
          );
        })}
      </div>

      <p className="text-sm text-[#3f4a47] leading-relaxed mb-8" data-testid="pa-hint">
        {ui.hint}
      </p>

      <button
        type="button"
        data-testid="pa-start-button"
        onClick={onStart}
        className="w-full sm:w-auto px-8 py-4 rounded-full font-semibold text-base text-white bg-[#b74e33] hover:bg-[#9c3f29] transition-colors"
      >
        {ui.start}
      </button>

      <p className="text-xs text-[#9a968a] leading-relaxed mt-10 pt-6 border-t border-black/10">{ui.archetypeNote}</p>
    </div>
  );
};

export default IntroScreen;
